import React, { useState, useEffect } from "react";

const ProfitMarginCalculator = () => {
  const [revenue, setRevenue] = useState(1000000);
  const [costOfGoodsSold, setCostOfGoodsSold] = useState(600000);
  const [operatingExpenses, setOperatingExpenses] = useState(200000);
  const [taxes, setTaxes] = useState(50000);
  const [otherExpenses, setOtherExpenses] = useState(20000);
  const [periodType, setPeriodType] = useState("monthly");
  const [result, setResult] = useState({
    grossProfit: "0.00",
    grossMargin: "0.00",
    operatingProfit: "0.00",
    operatingMargin: "0.00",
    netProfit: "0.00",
    netMargin: "0.00",
    totalCosts: "0.00",
    profitPerUnit: "0.00",
    marginCategory: "",
    businessHealth: "",
  });

  // Profit Margin Calculation Function
  const calculateProfitMargin = () => {
    const rev = parseFloat(revenue);
    const cogs = parseFloat(costOfGoodsSold);
    const opex = parseFloat(operatingExpenses);
    const tax = parseFloat(taxes);
    const other = parseFloat(otherExpenses);

    if (isNaN(rev) || rev <= 0) return;

    // Gross Profit
    const grossProfit = rev - cogs;
    const grossMargin = (grossProfit / rev) * 100;

    // Operating Profit (EBIT)
    const operatingProfit = grossProfit - opex;
    const operatingMargin = (operatingProfit / rev) * 100;

    // Net Profit
    const netProfit = operatingProfit - tax - other;
    const netMargin = (netProfit / rev) * 100;

    const totalCosts = cogs + opex + tax + other;
    const profitPerUnit = netProfit; // Can be divided by units if needed

    let marginCategory = "";
    let businessHealth = "";

    if (netMargin < 0) {
      marginCategory = "Loss Making";
      businessHealth = "Critical - Immediate action needed";
    } else if (netMargin < 5) {
      marginCategory = "Very Low";
      businessHealth = "Poor - Needs improvement";
    } else if (netMargin < 10) {
      marginCategory = "Low";
      businessHealth = "Below Average";
    } else if (netMargin < 15) {
      marginCategory = "Moderate";
      businessHealth = "Average Performance";
    } else if (netMargin < 20) {
      marginCategory = "Good";
      businessHealth = "Above Average";
    } else if (netMargin < 30) {
      marginCategory = "Very Good";
      businessHealth = "Strong Performance";
    } else {
      marginCategory = "Excellent";
      businessHealth = "Outstanding Performance";
    }

    setResult({
      grossProfit: grossProfit.toFixed(2),
      grossMargin: grossMargin.toFixed(2),
      operatingProfit: operatingProfit.toFixed(2),
      operatingMargin: operatingMargin.toFixed(2),
      netProfit: netProfit.toFixed(2),
      netMargin: netMargin.toFixed(2),
      totalCosts: totalCosts.toFixed(2),
      profitPerUnit: profitPerUnit.toFixed(2),
      marginCategory,
      businessHealth,
    });
  };

  useEffect(() => {
  calculateProfitMargin();
}, [revenue, costOfGoodsSold, operatingExpenses, taxes, otherExpenses]);



  return (
    <div className="calculator-wrapper">
      <div className="calculator-container">
        {/* Header */}
        <div className="calculator-header">
          <div className="header-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: 'white'}}>
              <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
            </svg>
          </div>
          <h1>Profit Margin Calculator</h1>
          <p>
            Calculate gross, operating, and net profit margins to analyze your business profitability
          </p>
        </div>

        {/* Main Calculator Card */}
        <div className="calculator-card">
          <div className="calculator-grid">
            {/* Left Side - Input Section */}
            <div className="input-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="1" x2="12" y2="23"></line>
                  <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                </svg>
                Revenue & Expense Details
              </h2>

              {/* Revenue */}
              <div className="input-group">
                <label>Total Revenue (Sales)</label>
                <div className="input-wrapper">
                  <span className="input-prefix">₹</span>
                  <input
                    type="number"
                    value={revenue}
                    onChange={(e) => setRevenue(e.target.value)}
                    className="calc-input"
                    placeholder="1000000"
                  />
                </div>
                <div className="quick-select-group">
                  {[500000, 1000000, 2000000, 5000000].map((amount) => (
                    <button
                      key={amount}
                      onClick={() => setRevenue(amount)}
                      className="quick-select-btn primary"
                    >
                      ₹{amount >= 100000 ? `${(amount / 100000).toFixed(0)}L` : `${(amount / 1000).toFixed(0)}K`}
                    </button>
                  ))}
                </div>
              </div>

              {/* Cost of Goods Sold */}
              <div className="input-group">
                <label>Cost of Goods Sold (COGS)</label>
                <div className="input-wrapper">
                  <span className="input-prefix">₹</span>
                  <input
                    type="number"
                    value={costOfGoodsSold}
                    onChange={(e) => setCostOfGoodsSold(e.target.value)}
                    className="calc-input"
                    placeholder="600000"
                  />
                </div>
                <div className="quick-select-group">
                  {[300000, 600000, 1000000, 2000000].map((amount) => (
                    <button
                      key={amount}
                      onClick={() => setCostOfGoodsSold(amount)}
                      className="quick-select-btn secondary"
                    >
                      ₹{amount >= 100000 ? `${(amount / 100000).toFixed(0)}L` : `${(amount / 1000).toFixed(0)}K`}
                    </button>
                  ))}
                </div>
              </div>

              {/* Operating Expenses */}
              <div className="input-group">
                <label>Operating Expenses (OPEX)</label>
                <div className="input-wrapper">
                  <span className="input-prefix">₹</span>
                  <input
                    type="number"
                    value={operatingExpenses}
                    onChange={(e) => setOperatingExpenses(e.target.value)}
                    className="calc-input"
                    placeholder="200000"
                  />
                </div>
                <div className="quick-select-group">
                  {[100000, 200000, 300000, 500000].map((amount) => (
                    <button
                      key={amount}
                      onClick={() => setOperatingExpenses(amount)}
                      className="quick-select-btn success"
                    >
                      ₹{amount >= 100000 ? `${(amount / 100000).toFixed(0)}L` : `${(amount / 1000).toFixed(0)}K`}
                    </button>
                  ))}
                </div>
              </div>

              {/* Period Type */}
              <div className="input-group">
                <label>Analysis Period</label>
                <div className="quick-select-group">
                  {[
                    { value: "monthly", label: "Monthly" },
                    { value: "annual", label: "Annual" }
                  ].map((type) => (
                    <button
                      key={type.value}
                      onClick={() => setPeriodType(type.value)}
                      className={`quick-select-btn ${periodType === type.value ? 'primary' : 'secondary'}`}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Taxes */}
              <div className="input-group">
                <label>Taxes</label>
                <div className="input-wrapper">
                  <span className="input-prefix">₹</span>
                  <input
                    type="number"
                    value={taxes}
                    onChange={(e) => setTaxes(e.target.value)}
                    className="calc-input"
                    placeholder="50000"
                  />
                </div>
                <div className="quick-select-group">
                  {[0, 30000, 50000, 100000].map((amount) => (
                    <button
                      key={amount}
                      onClick={() => setTaxes(amount)}
                      className="quick-select-btn success"
                    >
                      {amount === 0 ? "₹0" : `₹${(amount / 1000).toFixed(0)}K`}
                    </button>
                  ))}
                </div>
              </div>

              {/* Other Expenses */}
              <div className="input-group">
                <label>Other Expenses (Interest, etc.)</label>
                <div className="input-wrapper">
                  <span className="input-prefix">₹</span>
                  <input
                    type="number"
                    value={otherExpenses}
                    onChange={(e) => setOtherExpenses(e.target.value)}
                    className="calc-input"
                    placeholder="20000"
                  />
                </div>
                <div className="quick-select-group">
                  {[0, 10000, 20000, 50000].map((amount) => (
                    <button
                      key={amount}
                      onClick={() => setOtherExpenses(amount)}
                      className="quick-select-btn secondary"
                    >
                      {amount === 0 ? "₹0" : `₹${(amount / 1000).toFixed(0)}K`}
                    </button>
                  ))}
                </div>
              </div>

              <button onClick={calculateProfitMargin} className="calculate-btn">
                Calculate Profit Margins
              </button>
            </div>

            {/* Right Side - Results Section */}
            <div className="result-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <circle cx="12" cy="12" r="6"></circle>
                  <circle cx="12" cy="12" r="2"></circle>
                </svg>
                Profitability Analysis
              </h2>

              <div className="result-card">
                {/* Net Profit Margin - Highlighted */}
                <div className="result-item highlight">
                  <div className="result-item-header">
                    <span className="result-label">Net Profit Margin</span>
                    <div className="result-icon-wrapper white">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: 'white'}}>
                        <polyline points="22 7 13.5 15.5 8.5 10.5 2 17"></polyline>
                        <polyline points="16 7 22 7 22 13"></polyline>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    {result.netMargin}%
                  </div>
                  <div className="result-subtext">
                    {result.marginCategory} - {result.businessHealth}
                  </div>
                </div>

                {/* Gross Profit Margin */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Gross Profit Margin</span>
                    <div className="result-icon-wrapper blue">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#3b82f6'}}>
                        <line x1="12" y1="1" x2="12" y2="23"></line>
                        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    {result.grossMargin}%
                  </div>
                  <div className="result-subtext">
                    ₹{Number(result.grossProfit).toLocaleString("en-IN")} gross profit
                  </div>
                </div>

                {/* Operating Profit Margin */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Operating Profit Margin</span>
                    <div className="result-icon-wrapper purple">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#9333ea'}}>
                        <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"></polyline>
                        <polyline points="17 6 23 6 23 12"></polyline>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    {result.operatingMargin}%
                  </div>
                  <div className="result-subtext">
                    ₹{Number(result.operatingProfit).toLocaleString("en-IN")} operating profit
                  </div>
                </div>

                {/* Net Profit */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Net Profit</span>
                    <div className="result-icon-wrapper green">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#10b981'}}>
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.netProfit).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    After all expenses
                  </div>
                </div>

                {/* Visual Breakdown */}
                <div className="breakdown-section">
                  <h3 className="breakdown-title">Revenue Breakdown</h3>
                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">Revenue</span>
                      <span className="breakdown-percentage">
                        100% (₹{Number(revenue).toLocaleString("en-IN")})
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill green"
                        style={{
                          width: "100%",
                        }}
                      />
                    </div>
                  </div>
                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">Total Costs</span>
                      <span className="breakdown-percentage">
                        {((parseFloat(result.totalCosts) / revenue) * 100).toFixed(2)}% (₹{Number(result.totalCosts).toLocaleString("en-IN")})
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill blue"
                        style={{
                          width: `${(parseFloat(result.totalCosts) / revenue) * 100}%`,
                        }}
                      />
                    </div>
                  </div>
                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">Net Profit</span>
                      <span className="breakdown-percentage">
                        {result.netMargin}% (₹{Number(result.netProfit).toLocaleString("en-IN")})
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className={`breakdown-bar-fill ${parseFloat(result.netProfit) >= 0 ? 'green' : 'blue'}`}
                        style={{
                          width: `${Math.abs(parseFloat(result.netMargin))}%`,
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Profit Layers Box */}
                <div className="breakdown-section" style={{marginTop: '1.5rem', padding: '1rem', background: '#eff6ff', borderRadius: '8px', border: '1px solid #3b82f6'}}>
                  <h3 className="breakdown-title" style={{color: '#1e40af', marginBottom: '0.5rem'}}>Profit Layers</h3>
                  <div style={{fontSize: '0.875rem', color: '#1e3a8a', lineHeight: '1.6'}}>
                    <p style={{margin: '0.25rem 0'}}><strong>Gross:</strong> Revenue - COGS = ₹{Number(result.grossProfit).toLocaleString("en-IN")} ({result.grossMargin}%)</p>
                    <p style={{margin: '0.25rem 0'}}><strong>Operating:</strong> Gross - OPEX = ₹{Number(result.operatingProfit).toLocaleString("en-IN")} ({result.operatingMargin}%)</p>
                    <p style={{margin: '0.25rem 0'}}><strong>Net:</strong> Operating - Taxes & Other = ₹{Number(result.netProfit).toLocaleString("en-IN")} ({result.netMargin}%)</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Info Section */}
        <div className="info-section">
          <div className="info-header">
            <div className="info-icon-wrapper">
              <svg className="info-icon" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="16" x2="12" y2="12"></line>
                <line x1="12" y1="8" x2="12.01" y2="8"></line>
              </svg>
            </div>
            <div>
              <h3>Understanding Profit Margins</h3>
              <p>
                Profit margins are fundamental financial metrics that measure how much profit a business generates from 
                its revenue. They're expressed as percentages and indicate business efficiency, pricing power, and overall 
                financial health. There are three key profit margins: Gross Profit Margin shows profitability after 
                direct production costs (COGS), revealing pricing effectiveness and production efficiency. Operating Profit 
                Margin (EBIT margin) measures profitability after operating expenses, indicating operational efficiency. 
                Net Profit Margin is the bottom line - profit after all expenses including taxes and interest, showing 
                true profitability. Different industries have different typical margins: software companies often achieve 
                20-30% net margins due to low variable costs, retail typically sees 2-5% due to high competition, restaurants 
                average 3-6%, and manufacturing varies widely from 5-20%. Higher margins indicate better pricing power, 
                cost control, and competitive positioning. Tracking margins over time reveals business trends and guides 
                strategic decisions.
              </p>
            </div>
          </div>

          <div className="formula-section">
            <h4>Profit Margin Formulas:</h4>
            <div className="formula-display">
              Net Profit Margin = (Net Profit ÷ Revenue) × 100
            </div>

            <div className="formula-params">
              <div className="formula-param">
                <div className="formula-param-key">Gross Profit Margin</div>
                <div className="formula-param-value">(Revenue - COGS) ÷ Revenue × 100</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">Operating Profit Margin</div>
                <div className="formula-param-value">(Operating Profit) ÷ Revenue × 100</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">Net Profit Margin</div>
                <div className="formula-param-value">(Net Profit) ÷ Revenue × 100</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">EBITDA Margin</div>
                <div className="formula-param-value">Earnings before interest, taxes, depreciation & amortization</div>
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="faq-section">
            <h3>Frequently Asked Questions</h3>

            <div className="faq-item">
              <h5>What's the difference between gross, operating, and net profit margins?</h5>
              <p>
                These three margins reveal profitability at different business levels. Gross Profit Margin measures 
                profitability after direct production costs (Cost of Goods Sold). It's calculated as (Revenue - COGS) ÷ 
                Revenue × 100. This shows how efficiently you produce or acquire products and your basic pricing power. 
                High gross margins indicate strong pricing or low production costs. Operating Profit Margin (EBIT margin) 
                deducts operating expenses like salaries, rent, marketing, and R&D from gross profit. Formula: Operating 
                Profit ÷ Revenue × 100. This reveals operational efficiency and how well you control overhead costs. 
                Net Profit Margin is the final bottom line after all expenses including taxes, interest, and one-time 
                costs. Formula: Net Profit ÷ Revenue × 100.
              </p>
            </div>

            <div className="faq-item">
              <h5>What is a good profit margin for my business?</h5>
              <p>
                "Good" profit margins vary dramatically by industry, business model, and growth stage. Software/SaaS 
                companies typically achieve 15-30% net margins due to low marginal costs and scalability. Professional 
                services (consulting, legal) average 10-20%. E-commerce and retail see 2-5% due to intense competition 
                and thin margins. Restaurants operate at 3-6% net margins with 60-70% gross margins. Manufacturing ranges 
                from 5-20% depending on sector and automation level. Construction averages 3-7%. Healthcare varies widely 
                from 5-15%. New businesses often operate at negative margins during growth phases while established companies 
                should aim for industry benchmarks.
              </p>
            </div>

            <div className="faq-item">
              <h5>How can I improve my profit margins?</h5>
              <p>
                Improve margins through four strategic approaches. First, increase revenue without proportionally increasing 
                costs: raise prices where market allows (even 2-3% can significantly boost margins), implement value-based 
                pricing rather than cost-plus, add premium product tiers or services, introduce subscription/recurring revenue 
                models, or upsell/cross-sell to existing customers. Second, reduce Cost of Goods Sold: negotiate better 
                supplier terms through volume discounts or long-term contracts, find alternative suppliers or materials, 
                improve production efficiency and reduce waste, invest in automation to lower labor costs per unit, or 
                optimize inventory to reduce carrying costs. Third, decrease operating expenses: eliminate redundant processes, 
                negotiate lower rent or consider relocation, optimize marketing spend focusing on high-ROI channels, automate 
                administrative tasks, or renegotiate service contracts.
              </p>
            </div>

            <div className="faq-item">
              <h5>Why are my gross margins high but net margins low?</h5>
              <p>
                This common scenario indicates high operating expenses consuming gross profit. Start by analyzing your 
                expense structure. Calculate operating expenses as percentage of revenue - if above 30-40%, you likely 
                have overhead bloat. Common culprits include excessive staffing (too many employees for revenue level), 
                expensive real estate (rent/lease costs too high for business size), aggressive marketing spending without 
                ROI justification, redundant technology subscriptions, high administrative costs, or debt servicing eating 
                into profits. The solution involves systematic expense review. Benchmark each expense category against 
                industry standards. Identify non-essential spending that doesn't directly drive revenue. Implement zero-based 
                budgeting where every expense must be justified. Consider outsourcing non-core functions.
              </p>
            </div>

            <div className="faq-item">
              <h5>How do I compare profit margins across different businesses?</h5>
              <p>
                Comparing profit margins requires context and industry knowledge. First, always compare within the same 
                industry - comparing software margins (20%+) to grocery retail (1-2%) is meaningless due to completely 
                different business models. Use industry databases, trade associations, or competitor financial reports 
                (public companies) for benchmarks. Second, consider business maturity: startups often have negative margins 
                while investing in growth, whereas mature businesses should show steady margins. Third, adjust for business 
                model differences: asset-light businesses (consulting, software) naturally have higher margins than 
                capital-intensive ones (manufacturing, retail). Fourth, look at margin trends over time, not just absolute 
                numbers - improving margins signal effective management. Fifth, consider geographic differences: labor 
                costs, rent, and taxation vary by location affecting margins. Sixth, analyze margin quality: sustainable 
                margins from competitive advantages are better than temporary margins from cost cutting. Use multiple 
                metrics together - margins plus growth rate, return on assets, and cash flow provide comprehensive pictures.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfitMarginCalculator;